import React, { useEffect, useState, useMemo, useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  TablePagination,
  Paper,
  Stack,
  Chip,
  Button,
  TextField,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  alpha
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Add as AddIcon,
  PersonOutline as PersonIcon,
  Clear as ClearIcon
} from "@mui/icons-material";
import { debounce } from "@mui/material/utils";
import { toast } from "react-toastify";
import TopNav from "../components/topnav";
import { secureApi, ADMIN } from "../config";
import { Context } from "../context";
import { useNavigate } from "react-router-dom";
import UserModal from "../components/users/userModal";
import DeleteModal from "../components/users/deleteModal";

function createData(id, name, email, role, status) {
  return {
    id,
    name,
    email,
    role,
    status,
  };
}

const headCells = [
  {
    id: "no",
    label: "No",
  },
  {
    id: "name",
    label: "Name",
  },
  {
    id: "email",
    label: "Email",
  },
  {
    id: "role",
    label: "Role",
  },
  {
    id: "status",
    label: "Status",
  },
  {
    id: "operation",
    label: "Actions",
  },
];

export default function Users() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setLoggedIn, user } = useContext(Context);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(10);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userID, setUserID] = useState();
  const [searchStr, setSearchStr] = useState("");
  const [loading, setLoading] = useState(true);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddButtonClick = () => {
    setUserID(null);
    setUserModalOpen(true);
  };

  const handleCancel = () => {
    setUserModalOpen(false);
    setUserID(null);
  };

  const handleEditButtonClick = (event, user_id) => {
    setUserID(user_id);
    setUserModalOpen(true);
  };

  const handleDeleteButtonClick = (event, user_id) => {
    setUserID(user_id);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const clearSearch = () => {
    setSearchStr("");
  };

  useEffect(() => {
    setLoading(true);
    secureApi(window.localStorage.getItem("alex-med-token"))
      .post(`/users`, {
        page,
        rowsPerPage,
        searchStr,
      })
      .then((response) => {
        let { users, total_users } = response.data;
        users = users.map((user) =>
          createData(
            user["_id"],
            user["name"],
            user["email"],
            user["role"],
            user["status"]
          )
        );
        setUsers(users);
        setTotalUsers(total_users);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else {
          toast.error(e.response?.data?.message || "Failed to load users");
        }
      });
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (!user || user.role !== ADMIN) {
      toast.warn("You don't have permission to access this page!");
      navigate("/");
    }
  }, [user]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalUsers) : 0;

  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        setLoading(true);
        secureApi(window.localStorage.getItem("alex-med-token"))
          .post(`/users`, {
            page: 0,
            rowsPerPage,
            searchStr: request.searchStr,
          })
          .then((response) => {
            callback(response.data);
            setLoading(false);
          })
          .catch((e) => {
            console.log(e);
            setLoading(false);
            if (e.response && e.response.status === 401) {
              toast.warn("Session has been expired. You need to login again!");
              setLoggedIn(false);
              navigate("/login");
            } else {
              toast.error(e.response?.data?.message || "Failed to search users");
            }
          });
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    fetch({ searchStr: searchStr }, (results) => {
      if (active) {
        let { users, total_users } = results;
        users = users.map((user) =>
          createData(
            user["_id"],
            user["name"],
            user["email"],
            user["role"],
            user["status"]
          )
        );
        setUsers(users);
        setTotalUsers(total_users);
        setPage(0);
      }
    });

    return () => {
      active = false;
    };
  }, [searchStr, fetch]);

  const getRoleChip = (role) => {
    let color = "primary";
    let variant = "filled";
    
    switch(role.toLowerCase()) {
      case "admin":
        color = "primary";
        break;
      case "doctor":
        color = "info";
        break;
      case "medical_assistant":
        color = "secondary";
        break;
      case "receptionist":
        color = "success";
        break;
      default:
        color = "default";
    }
    
    return (
      <Chip 
        label={role} 
        color={color} 
        variant={variant}
        size="small"
        sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}
      />
    );
  };

  return (
    <>
      <TopNav item="users" />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Box 
            sx={{ 
              p: 3, 
              bgcolor: theme.palette.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2
            }}
          >
            <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
              <PersonIcon sx={{ mr: 1 }} />
              User Management
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, width: isMobile ? '100%' : 'auto' }}>
              <TextField
                size="small"
                placeholder="Search users..."
                variant="outlined"
                value={searchStr}
                onChange={(e) => setSearchStr(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'white' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchStr && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch} sx={{ color: 'white' }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: alpha('#fff', 0.15),
                    borderRadius: '8px',
                    color: 'white',
                    '&:hover': {
                      bgcolor: alpha('#fff', 0.25),
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'transparent'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'white'
                    },
                    '&::placeholder': {
                      color: alpha('#fff', 0.7)
                    }
                  }
                }}
                sx={{ 
                  minWidth: 250,
                  '& .MuiInputLabel-root': {
                    color: alpha('#fff', 0.7)
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'white'
                  }
                }}
              />
              
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={handleAddButtonClick}
                sx={{ 
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  px: 2
                }}
              >
                Add User
              </Button>
            </Box>
          </Box>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="users table" size="medium">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.id === 'operation' ? 'center' : 'left'}
                      sx={{
                        fontWeight: 'bold',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.text.primary,
                        py: 2
                      }}
                    >
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && users.map((row, index) => {
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.id}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {row.name}
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        {getRoleChip(row.role)}
                      </TableCell>
                      <TableCell>
                        {row.status === 1 ? (
                          <Chip 
                            label="Active" 
                            color="success" 
                            variant="filled" 
                            size="small"
                            sx={{ fontWeight: 'medium' }}
                          />
                        ) : (
                          <Chip 
                            label="Inactive" 
                            color="error" 
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 'medium' }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Button
                            color="info"
                            variant="contained"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => handleEditButtonClick(e, row["id"])}
                            sx={{ 
                              borderRadius: '8px', 
                              textTransform: 'none',
                              boxShadow: 'none',
                              '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            color="error"
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={(e) => handleDeleteButtonClick(e, row["id"])}
                            sx={{ 
                              borderRadius: '8px', 
                              textTransform: 'none',
                              '&:hover': { 
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                borderColor: theme.palette.error.main
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {loading && Array.from(new Array(rowsPerPage)).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={6} sx={{ py: 5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          Loading users...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                
                {!loading && totalUsers < 1 && (
                  <TableRow style={{ height: 300 }}>
                    <TableCell
                      colSpan={6}
                      sx={{ 
                        textAlign: "center", 
                        py: 8,
                        color: theme.palette.text.secondary
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                      <Typography variant="h6">No users found</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {searchStr ? 'Try adjusting your search' : 'Add a new user to get started'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: theme.palette.text.secondary
              }
            }}
          />
        </Paper>

        <UserModal
          open={userModalOpen}
          handleCancel={handleCancel}
          userID={userID}
        />
        <DeleteModal
          open={deleteModalOpen}
          handleCancel={handleDeleteCancel}
          userID={userID}
        />
      </Container>
    </>
  );
}